(() => {
  const existingApi = window.BPUniverse;

  if (existingApi?.stateStoreVersion === '2') {
    existingApi.synchronize({ source: 'controller-reinitialize', transition: false });
    window.BPGlobalUniverse = existingApi;
    window.dispatchEvent(
      new CustomEvent('bp:universe-controller-ready', {
        detail: { state: existingApi.getState(), timestamp: new Date().toISOString() },
      }),
    );
    return;
  }

  const effectsApi = existingApi;
  const allowedUniverses = new Set(['neutral', 'mystery', 'bpc', 'beautyves', 'lip', 'event']);
  const storageKey = 'bp:global-universe:v2';
  const homepageContentSelector = '[data-bp-universe-content]';
  const legacyHomepageSections = Object.freeze({
    featured_collection_J3rfNd: 'beautyves',
    featured_collection_98txhK: 'beautyves',
    featured_collection_ximf6V: 'mystery',
  });
  const gatewaySelector = '[data-bp-brand-gateway]';
  const gatewayPanelSelector = '[data-bp-gateway-panel]';
  const headerSelector = '[data-bp-universe-surface="header"]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const subscribers = new Set();
  const editorSources = new Set([
    'theme-editor',
    'theme-editor-block',
    'theme-editor-section',
    'preview-inspector',
  ]);
  let currentUniverse = 'neutral';
  let currentSource = 'bootstrap';
  let ready = false;
  let initialized = false;
  let editorRestoreUniverse = null;
  let activeTransition = null;
  let initialGatewayPending = false;

  const body = () => document.body;
  const isHomepage = () => body()?.dataset.bpHomepage === 'true';
  const isDesignMode = () => body()?.dataset.bpDesignMode === 'true' || Boolean(window.Shopify?.designMode);
  const eventAvailable = () => document.querySelector('[data-bp-daily-event][data-bp-event-valid="true"], [data-bp-brand-gateway][data-bp-event-active="true"] .bp-bf-gateway-card') !== null;
  const normalizeUniverse = (value) => {
    if (value === 'event' && !eventAvailable()) return 'mystery';
    if (value === 'lip' && isHomepage() && document.querySelector(gatewaySelector) &&
        !document.querySelector(`${gatewaySelector} ${gatewayPanelSelector}[data-universe="lip"]`)) return 'mystery';
    return allowedUniverses.has(value) ? value : 'neutral';
  };
  const carouselPosition = (index, activeIndex, count) => {
    if (activeIndex < 0) return 'neutral';
    const distance = (index - activeIndex + count) % count;
    if (distance === 0) return 'front';
    if (distance === 1) return 'right';
    if (distance === count - 1) return 'left';
    if (count === 5) return distance === 2 ? 'far-right' : 'far-left';
    return 'far';
  };

  const setAttributeState = (element, name, enabled) => {
    if (!element) return;
    if (typeof element.toggleAttribute === 'function') {
      element.toggleAttribute(name, enabled);
    } else if (enabled) {
      element.setAttribute?.(name, '');
    } else {
      element.removeAttribute?.(name);
    }
  };

  const setReadyState = (nextReady) => {
    ready = Boolean(nextReady);
    const value = String(ready);
    if (document.documentElement?.dataset) document.documentElement.dataset.bpUniverseReady = value;
    if (body()?.dataset) body().dataset.bpUniverseReady = value;
    document.querySelectorAll(gatewaySelector).forEach((gateway) => {
      gateway.dataset.bpUniverseReady = value;
    });
  };

  const readStoredUniverse = () => {
    if (!isHomepage() || isDesignMode()) return null;

    try {
      const stored = window.sessionStorage.getItem(storageKey);
      return allowedUniverses.has(stored) ? stored : null;
    } catch (_error) {
      return null;
    }
  };

  const storeUniverse = (universe, source) => {
    if (!isHomepage() || isDesignMode() || editorSources.has(source)) return;

    try {
      if (universe === 'neutral') window.sessionStorage.removeItem?.(storageKey);
      else window.sessionStorage.setItem(storageKey, universe);
    } catch (_error) {
      // Storage can be unavailable in privacy modes; current-page state still works.
    }
  };

  const configuredGatewayUniverse = () => {
    const gateway = document.querySelector(gatewaySelector);
    if (!gateway) return 'neutral';

    const isDesktop = window.matchMedia('(min-width: 900px)').matches;
    if (!isDesktop) return normalizeUniverse(gateway.dataset.mobileInitial);
    if (gateway.dataset.desktopState === 'active') return normalizeUniverse(gateway.dataset.defaultUniverse);
    return 'neutral';
  };

  const resolveInitialUniverse = (fallbackUniverse) => {
    const pageBody = body();
    if (!pageBody) return 'neutral';
    if (!isHomepage()) return normalizeUniverse(pageBody.dataset.bpDirectUniverse);
    if (isDesignMode()) return normalizeUniverse(fallbackUniverse ?? configuredGatewayUniverse());
    const homepageInitial = document.querySelector(gatewaySelector)?.dataset.homepageInitial;
    return readStoredUniverse() || normalizeUniverse(homepageInitial);
  };

  const announceUniverse = (universe) => {
    const liveRegion = document.querySelector('[data-bp-universe-live]');
    if (!liveRegion) return;

    const label = liveRegion.dataset[`label${universe.charAt(0).toUpperCase()}${universe.slice(1)}`];
    liveRegion.textContent = label || '';
  };

  const synchronizeContent = (universe) => {
    if (isHomepage()) {
      for (const [id, owner] of Object.entries(legacyHomepageSections)) {
        document.querySelectorAll(`[id$="__${id}"]`).forEach((section) => {
          section.dataset.bpUniverseContent = owner;
        });
      }
    }
    document.querySelectorAll(homepageContentSelector).forEach((content) => {
      const isActive = isHomepage() && universe !== 'neutral' && content.dataset.bpUniverseContent === universe;
      content.hidden = !isActive;
      content.setAttribute('aria-hidden', String(!isActive));
      content.inert = !isActive;
      setAttributeState(content, 'inert', !isActive);
    });
  };

  const synchronizeGateway = (gateway, universe, options = {}) => {
    if (!gateway) return;
    const editorial = gateway.dataset.bpEditorialGateway !== undefined;
    const panels = Array.from(gateway.querySelectorAll(gatewayPanelSelector)).filter((panel) => !panel.hidden);
    const activePanel = panels.find((panel) => panel.dataset.universe === universe) || null;
    const activeIndex = panels.indexOf(activePanel);
    const carousel = gateway.dataset.bpCarousel !== undefined && panels.length >= 3 && panels.length <= 5;

    gateway.dataset.bpUniverse = activePanel?.dataset.universe || 'neutral';
    gateway.classList.toggle('has-active-panel', Boolean(activePanel));

    panels.forEach((panel, index) => {
      const isActive = panel === activePanel;
      const trigger = panel.querySelector('[data-bp-gateway-trigger]');
      const content = panel.querySelector('[data-bp-gateway-content]');

      panel.classList.toggle('is-active', isActive);
      if (carousel) {
        panel.dataset.bpCarouselPosition = carouselPosition(index, activeIndex, panels.length);
      }
      trigger?.setAttribute('aria-expanded', String(isActive));
      trigger?.setAttribute(editorial ? 'aria-pressed' : 'aria-selected', String(isActive));
      if (trigger) trigger.tabIndex = editorial ? 0 : activePanel ? (isActive ? 0 : -1) : 0;

      const details = carousel ? panel.querySelector('[data-bp-gateway-details]') : null;
      if (details) {
        details.hidden = !isActive;
        details.inert = !isActive;
        details.setAttribute('aria-hidden', String(!isActive));
        setAttributeState(details, 'inert', !isActive);
      }

      if (content) {
        content.hidden = !isActive;
        content.setAttribute('aria-hidden', String(!isActive));
        content.inert = !isActive;
        setAttributeState(content, 'inert', !isActive);
        content.querySelectorAll('a').forEach((link) => {
          link.tabIndex = isActive ? 0 : -1;
        });
      }
    });

    if (options.announce && activePanel) {
      const liveRegion = gateway.querySelector('[data-bp-gateway-live]');
      if (liveRegion) liveRegion.textContent = activePanel.dataset.selectionAnnouncement || '';
    }
  };

  const gatewayIsCoherent = (gateway, universe) => {
    const selectionAttribute = gateway.dataset.bpEditorialGateway !== undefined ? 'aria-pressed' : 'aria-selected';
    const panels = Array.from(gateway.querySelectorAll(gatewayPanelSelector)).filter((panel) => !panel.hidden);
    const activePanels = panels.filter((panel) => panel.classList.contains('is-active'));
    const selectedTriggers = panels.filter(
      (panel) => panel.querySelector('[data-bp-gateway-trigger]')?.getAttribute(selectionAttribute) === 'true',
    );
    const expandedTriggers = panels.filter(
      (panel) => panel.querySelector('[data-bp-gateway-trigger]')?.getAttribute('aria-expanded') === 'true',
    );

    if (gateway.dataset.bpUniverse !== universe) return false;
    if (gateway.dataset.bpCarousel !== undefined && panels.length >= 3 && panels.length <= 5) {
      const activeIndex = panels.findIndex(panel => panel.dataset.universe === universe);
      if (!panels.every((panel, index) => {
        const active = index === activeIndex;
        const position = carouselPosition(index, activeIndex, panels.length);
        const details = panel.querySelector('[data-bp-gateway-details]');
        return panel.dataset.bpCarouselPosition === position && details &&
          details.hidden === !active && details.inert === !active &&
          details.getAttribute('aria-hidden') === String(!active);
      })) return false;
    }
    if (universe === 'neutral') return activePanels.length === 0 && selectedTriggers.length === 0 && expandedTriggers.length === 0;
    return (
      activePanels.length === 1 &&
      selectedTriggers.length === 1 &&
      expandedTriggers.length === 1 &&
      activePanels[0].dataset.universe === universe &&
      selectedTriggers[0] === activePanels[0] &&
      expandedTriggers[0] === activePanels[0]
    );
  };

  const contentIsCoherent = (universe) => {
    const contents = Array.from(document.querySelectorAll(homepageContentSelector));
    if (!isHomepage() || !contents.length) return true;
    const visible = contents.filter((content) => !content.hidden && content.getAttribute('aria-hidden') === 'false');
    if (universe === 'neutral') return visible.length === 0;
    return visible.length > 0 && visible.every((content) => content.dataset.bpUniverseContent === universe);
  };

  const domIsCoherent = (universe) => {
    const pageBody = body();
    if (!pageBody || pageBody.dataset.bpUniverse !== universe) return false;
    if (document.documentElement?.dataset.bpUniverse !== universe) return false;
    if (!contentIsCoherent(universe)) return false;
    return Array.from(document.querySelectorAll(gatewaySelector)).every((gateway) => gatewayIsCoherent(gateway, universe));
  };

  const getState = () => Object.freeze({ universe: currentUniverse, source: currentSource, ready });

  const notifySubscribers = () => {
    const state = getState();
    subscribers.forEach((callback) => callback(state));
  };

  const dispatchStateChange = () => {
    window.dispatchEvent(
      new CustomEvent('bp:universe-state-change', {
        detail: { ...getState(), timestamp: new Date().toISOString() },
      }),
    );
  };

  // Decoration must never queue state changes or leave a ViewTransition promise unhandled.
  const runBPViewTransition = (updateCallback, metadata = {}) => {
    if (activeTransition) {
      if (!activeTransition.superseded) {
        activeTransition.superseded = true;
        try {
          activeTransition.transition?.skipTransition?.();
        } catch (error) {
          console.error('[BPUniverse] skipTransition failed', error);
        }
      }
      body()?.classList.remove('bp-universe-view-transitions');
      // skipTransition still runs the old callback: its guard below prevents stale writes.
      return updateCallback();
    }

    if (metadata.transition === false || isDesignMode() || reducedMotion.matches ||
        typeof document.startViewTransition !== 'function') return updateCallback();

    const job = { superseded: false, updated: false, callbackError: null, reported: new Set() };
    activeTransition = job;
    const cleanup = () => {
      if (activeTransition !== job) return;
      activeTransition = null;
      body()?.classList.remove('bp-universe-view-transitions');
    };
    const report = (error, phase) => {
      // Only ready's platform skip AbortError is expected; callback errors remain real errors.
      if (phase === 'ready' && error?.name === 'AbortError' && error !== job.callbackError) return;
      if (job.reported.has(error)) return;
      job.reported.add(error);
      console.error(`[BPUniverse] ViewTransition ${phase} failed`, error);
    };
    const update = () => {
      if (job.superseded || job.updated) return;
      job.updated = true;
      try {
        const result = updateCallback();
        if (result?.then) return Promise.resolve(result).catch((error) => {
          job.callbackError = error;
          throw error;
        });
        return result;
      } catch (error) {
        job.callbackError = error;
        throw error;
      }
    };

    body()?.classList.add('bp-universe-view-transitions');
    try {
      job.transition = document.startViewTransition(update);
      ['ready', 'updateCallbackDone', 'finished'].forEach((phase) => {
        Promise.resolve(job.transition[phase]).then(
          () => { if (phase === 'finished') cleanup(); },
          (error) => { report(error, phase); if (phase === 'finished') cleanup(); },
        );
      });
    } catch (error) {
      report(error, 'start');
      cleanup();
      return update();
    }
  };

  const applyUniverseState = (requestedUniverse, metadata = {}) => {
    const pageBody = body();
    if (!pageBody) return 'neutral';

    const directUniverse = normalizeUniverse(pageBody.dataset.bpDirectUniverse);
    const requested = normalizeUniverse(requestedUniverse);
    const universe = isHomepage() ? requested : directUniverse;
    const source = metadata.source || 'global-universe-controller';
    if (metadata.persist || document.querySelector(gatewaySelector)) initialGatewayPending = false;
    const canTransition =
      metadata.transition !== false &&
      !document.querySelector('[data-bp-carousel]') &&
      universe !== currentUniverse &&
      initialized &&
      !isDesignMode() &&
      !reducedMotion.matches &&
      typeof document.startViewTransition === 'function';

    setReadyState(false);
    currentUniverse = universe;
    currentSource = source;

    const commit = () => {
      pageBody.dataset.bpUniverse = universe;
      if (document.documentElement?.dataset) document.documentElement.dataset.bpUniverse = universe;
      pageBody.classList.toggle('bp-universe-selection-active', isHomepage() && universe !== 'neutral');
      document.querySelectorAll(headerSelector).forEach((header) => {
        header.dataset.bpUniverse = universe;
      });
      synchronizeContent(universe);
      document.querySelectorAll(gatewaySelector).forEach((gateway) => synchronizeGateway(gateway, universe, metadata));
      if (metadata.announce) announceUniverse(universe);

      setReadyState(domIsCoherent(universe));
      notifySubscribers();
      setReadyState(domIsCoherent(universe));
      dispatchStateChange();
    };

    runBPViewTransition(commit, { transition: canTransition });

    if (metadata.persist) storeUniverse(universe, source);
    initialized = true;
    return universe;
  };

  const setState = (universe, metadata = {}) => applyUniverseState(universe, metadata);
  const synchronize = (metadata = {}) =>
    applyUniverseState(!initialized || (initialGatewayPending && document.querySelector(gatewaySelector)) ?
      resolveInitialUniverse() : currentUniverse, {
      transition: false,
      ...metadata,
    });
  const isReady = () => ready;
  const subscribe = (callback) => {
    if (typeof callback !== 'function') return () => {};
    subscribers.add(callback);
    callback(getState());
    return () => subscribers.delete(callback);
  };
  const unsubscribe = (callback) => subscribers.delete(callback);

  const universeFromEditorTarget = (target) => {
    const panel = target?.closest?.(gatewayPanelSelector) || target?.querySelector?.(gatewayPanelSelector);
    if (panel) return normalizeUniverse(panel.dataset.universe);
    const content = target?.closest?.(homepageContentSelector) || target?.querySelector?.(homepageContentSelector);
    return content ? normalizeUniverse(content.dataset.bpUniverseContent) : null;
  };

  const handleUniverseChange = (event) => {
    if (!isHomepage() || event.detail?.source !== 'brand-gateway' || event.detail?.stateApplied) return;
    setState(event.detail?.universe, { persist: true, announce: true, source: 'brand-gateway' });
  };

  const handleGatewayReady = () => {
    if (!isHomepage()) return;
    synchronize({ source: 'gateway-ready', transition: false });
  };

  const handleEditorSelect = (event) => {
    if (!isHomepage() || !isDesignMode()) return;
    const universe = universeFromEditorTarget(event.target);
    if (!universe || universe === 'neutral') return;

    if (editorRestoreUniverse === null) editorRestoreUniverse = currentUniverse;
    setState(universe, { transition: false, announce: false, source: 'theme-editor' });
  };

  const handleEditorDeselect = () => {
    if (editorRestoreUniverse === null) return;
    const restoreUniverse = editorRestoreUniverse;
    editorRestoreUniverse = null;
    setState(restoreUniverse, { transition: false, announce: false, source: 'theme-editor' });
  };

  const initializeController = () => {
    if (!body()) return;
    initialGatewayPending = isHomepage() && !document.querySelector(gatewaySelector);
    setState(resolveInitialUniverse(), {
      transition: false,
      announce: false,
      source: initialized ? 'controller-reinitialize' : 'controller-initialization',
    });
  };

  document.addEventListener('bp:universe-change', handleUniverseChange);
  document.addEventListener('bp:gateway-ready', handleGatewayReady);
  document.addEventListener('shopify:section:load', () => synchronize({ source: 'section-load' }));
  document.addEventListener('shopify:section:unload', () => synchronize({ source: 'section-unload' }));
  document.addEventListener('shopify:section:select', handleEditorSelect);
  document.addEventListener('shopify:section:deselect', handleEditorDeselect);
  document.addEventListener('shopify:block:select', handleEditorSelect);
  document.addEventListener('shopify:block:deselect', handleEditorDeselect);

  const api = Object.freeze({
    stateStoreVersion: '2',
    getState,
    setState,
    subscribe,
    unsubscribe,
    isReady,
    synchronize,
    synchronizeGateway,
    normalizeUniverse,
    resolveInitialUniverse,
    applyUniverse: applyUniverseState,
    applyUniverseState,
    runBPViewTransition,
    initialize: (root = document) => effectsApi?.initialize?.(root),
    destroy: (root) => effectsApi?.destroy?.(root),
  });

  window.BPUniverse = api;
  window.BPGlobalUniverse = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeController, { once: true });
  } else {
    initializeController();
  }

  window.dispatchEvent(
    new CustomEvent('bp:universe-controller-ready', {
      detail: { state: getState(), timestamp: new Date().toISOString() },
    }),
  );
})();
